import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Flex, Group, Stack, Text, Title } from '@mantine/core';

import { useAppDispatch } from '@/store/hooks';
import { patientsApiSlice } from '@/store/slices/api';
import { useCreateAppointmentMutation } from '@/store/slices/api/schedules/schedulesApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import styles from '../DeviceScheduleModal.module.scss';
import { EditScheduleStepProps } from '../types';

const SetERITracking: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  setLoading,
  deviceInfo
}) => {
  const { id: patientId } = useParams() as { id: string };

  const [showSummary, setShowSummary] = useState(false);
  const dispatch = useAppDispatch();

  const [createAppointment] = useCreateAppointmentMutation();

  const handleConfirm = async () => {
    if (!deviceInfo) {
      return;
    }

    setLoading(true);
    try {
      await createAppointment({
        deviceId: deviceInfo.deviceId,
        type: 'ERI Tracking',
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      }).unwrap();

      dispatch(patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patientId }]));

      setShowSummary(true);
    } catch (error) {
      console.error('Error setting ERI tracking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    onClose();
  };

  if (showSummary) {
    return (
      <Stack gap="xs">
        <Flex px={20} py={16} direction={'column'} gap={4}>
          <Title order={5} fw={600} c={'plum.9'}>
            ERI Tracking Set Successfully
          </Title>
          <Text size="sm" c={'plum'}>
            ERI tracking has been successfully added to the device.
          </Text>
        </Flex>
        <Group className={styles.buttonGroup} justify="flex-end">
          <Button color="plum.9" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Flex px={20} py={16} direction={'column'} gap={4}>
        <Title order={5} fw={600} c={'plum.9'}>
          Set ERI Tracking
        </Title>
        <Text size="sm" c={'plum'}>
          This will set up ERI tracking schedule for device and will be configured based on account
          and patient settings.
        </Text>
      </Flex>

      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          disabled={loading}
          className={styles.modalButton}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleConfirm}
          loading={loading}
          className={styles.modalButton}>
          Confirm
        </Button>
      </Group>
    </Stack>
  );
};

export default SetERITracking;
